import { useEffect, useState } from "react"
import { Mezo } from "@mezo/sdk"
import { OrangeKitSmartAccount } from "@mezo-org/orangekit-smart-account"
import { BrowserProvider, JsonRpcSigner } from "ethers"
import { providers } from "ethers-v5"
import type { Account, Chain, Client, Transport } from "viem"
import { type Config, Connector, useAccount, useConnectorClient } from "wagmi"

import { MEZO_CHAIN } from "../constants"

export type EthersV6Signer = JsonRpcSigner
export type EthersV5Signer = providers.JsonRpcSigner

// TODO: at some point Passport will probably rename type to "passport"
export const isPassportConnector = (connector: Connector) =>
  connector.type === "orangekit"

export async function clientToSignerEthersV5(
  client: Client<Transport, Chain, Account>,
  connector: Connector,
): Promise<EthersV5Signer> {
  const { account, chain, transport } = client

  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  }

  const provider = new providers.Web3Provider(transport, network)

  if (isPassportConnector(connector)) {
    const orangeKitSmartAccount = await connector?.getProvider({
      chainId: chain.id,
    })

    // @ts-expect-error types are not matching but it should work regardless
    const bitcoinWalletProvider = connector?.getBitcoinProvider()

    if (!orangeKitSmartAccount || !bitcoinWalletProvider) {
      throw new Error(
        "Failed to get v5 ethers signer from passport connector: Either Orangekit Smart Account or Bitcoin Provider is undefined",
      )
    }

    const ethersSigner = await Mezo.toEthersSigner(
      // types are not matching but it should work regardless
      provider as unknown as BrowserProvider,
      account.address,
      orangeKitSmartAccount as OrangeKitSmartAccount,
      bitcoinWalletProvider,
    )

    // @ts-expect-error types are not matching but it should work regardless
    return ethersSigner
  }

  return provider.getSigner()
}

/** Hook to convert a Viem Client to an ethers.js Signer. */
export function useEthersV5Signer({ chainId }: { chainId?: number } = {}):
  | EthersV5Signer
  | undefined {
  const { connector } = useAccount()
  const { data: client } = useConnectorClient<Config>({ chainId, connector })

  const [signer, setSigner] = useState<EthersV5Signer | undefined>(undefined)

  useEffect(() => {
    const getSigner = async () => {
      const ethersV5Signer =
        client && connector
          ? await clientToSignerEthersV5(
              client as Client<Transport, Chain, Account>,
              connector,
            )
          : undefined

      setSigner(ethersV5Signer)
    }

    getSigner()
  }, [client, connector])

  return signer
}

export async function clientToSignerEthersV6(
  client: Client<Transport, Chain, Account>,
  connector: Connector,
): Promise<EthersV6Signer> {
  const { account, chain, transport } = client

  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  }

  const provider = new BrowserProvider(transport, network)

  if (isPassportConnector(connector)) {
    const orangeKitSmartAccount = await connector?.getProvider()
    // @ts-expect-error types are not matching but it should work regardless
    const bitcoinWalletProvider = connector?.getBitcoinProvider()

    if (!orangeKitSmartAccount || !bitcoinWalletProvider) {
      throw new Error(
        "Failed to get v6 ethers signer from passport connector: Either Orangekit Smart Account or Bitcoin Provider is undefined",
      )
    }
    const providerChainId = Number((await provider.getNetwork()).chainId)

    let ethersSigner

    if (providerChainId === MEZO_CHAIN.id) {
      ethersSigner = await Mezo.toMezoEthersSigner(
        provider,
        account.address,
        bitcoinWalletProvider,
      )
    } else {
      ethersSigner = await Mezo.toEthersSigner(
        provider,
        account.address,
        orangeKitSmartAccount as OrangeKitSmartAccount,
        bitcoinWalletProvider,
      )
    }

    // @ts-expect-error types are not matching but it should work regardless
    return ethersSigner
  }

  return new JsonRpcSigner(provider, account.address)
}

/** Hook to convert a viem Wallet Client to an ethers.js Signer. */
export function useEthersV6Signer({ chainId }: { chainId?: number } = {}):
  | EthersV6Signer
  | undefined {
  const { connector } = useAccount()
  const { data: client } = useConnectorClient<Config>({ chainId, connector })

  const [signer, setSigner] = useState<EthersV6Signer | undefined>(undefined)

  useEffect(() => {
    const getSigner = async () => {
      const ethersV6Signer =
        client && connector
          ? await clientToSignerEthersV6(
              client as Client<Transport, Chain, Account>,
              connector,
            )
          : undefined

      setSigner(ethersV6Signer)
    }

    getSigner()
  }, [client, connector])

  return signer
}
